import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  ShieldCheckIcon,
  CheckCircleIcon,
  ArrowRightIcon,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialState = {
  full_name: "",
  work_email: "",
  law_firm: "",
  role: "",
  country: "",
  target_directory: "",
  practice_area: "",
  next_submission_cycle: "",
  main_challenge: "",
  confidentiality_ack: false,
  website: "", // honeypot — must remain empty
};

export default function EarlyAccessForm() {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Required";
    if (!form.work_email.trim()) e.work_email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.work_email))
      e.work_email = "Enter a valid work email";
    if (!form.law_firm.trim()) e.law_firm = "Required";
    if (!form.role.trim()) e.role = "Required";
    if (!form.country.trim()) e.country = "Required";
    if (!form.target_directory) e.target_directory = "Required";
    if (!form.practice_area.trim()) e.practice_area = "Required";
    if (!form.next_submission_cycle.trim())
      e.next_submission_cycle = "Required";
    if (!form.main_challenge.trim()) e.main_challenge = "Required";
    if (!form.confidentiality_ack)
      e.confidentiality_ack = "You must acknowledge before submitting.";
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length > 0) {
      toast.error("Please complete the required fields.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSubmitted(true);
      toast.success("Request received. We’ll be in touch.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section
        id="early-access"
        data-testid="early-access-section"
        className="relative py-24 md:py-32 overflow-hidden bg-[#f7f4ec]"
      >
        <div
          aria-hidden="true"
          className="absolute -top-20 right-0 h-80 w-80 rounded-full bg-[#ff5e3a]/15 blur-[110px]"
        />
        <div className="relative z-10 max-w-2xl mx-auto px-6 md:px-12">
          <div
            data-testid="form-success"
            className="rounded-3xl bg-[#f3efe6] border border-stone-200 p-10 md:p-14 text-center shadow-sm"
          >
            <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-stone-900 text-[#f7f4ec] mb-6">
              <CheckCircleIcon size={28} weight="duotone" />
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-stone-900">
              Request received.
            </h2>
            <p className="mt-4 text-stone-700 leading-relaxed">
              Thank you. We review every early-access request manually. If your
              firm is invited to early access, our team will reach out with the
              next steps before your upcoming submission cycle.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="early-access"
      data-testid="early-access-section"
      className="relative py-24 md:py-32 overflow-hidden bg-[#f7f4ec]"
    >
      <div
        aria-hidden="true"
        className="absolute -top-20 right-0 h-[360px] w-[360px] rounded-full bg-[#ff5e3a]/18 blur-[120px]"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-32 -left-20 h-[360px] w-[360px] rounded-full bg-[#ffb59a]/25 blur-[120px]"
      />

      <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12">
        <div className="text-center mb-10">
          <p className="text-sm uppercase tracking-[0.18em] font-semibold text-stone-500">
            Early access form
          </p>
          <h2 className="mt-4 font-heading text-3xl md:text-5xl tracking-tight font-medium text-stone-900">
            Request early access to Ranq.
          </h2>
          <p
            data-testid="form-confidentiality-note"
            className="mt-4 text-stone-700 leading-relaxed"
          >
            Lead qualification only. Please do not include confidential matter
            details, client names, documents or case information.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="early-access-form"
          className="relative rounded-3xl bg-[#f3efe6]/80 backdrop-blur-xl border border-stone-200 p-7 md:p-10 shadow-sm"
          noValidate
        >
          {/* Honeypot — hidden from users, bots will fill it */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-10000px",
              top: "auto",
              width: "1px",
              height: "1px",
              overflow: "hidden",
            }}
          >
            <label htmlFor="website-hp">Website</label>
            <input
              id="website-hp"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={(e) => update("website", e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Full name" error={errors.full_name}>
              <Input
                data-testid="form-full-name"
                value={form.full_name}
                onChange={(e) => update("full_name", e.target.value)}
                placeholder="Jane Doe"
                autoComplete="name"
                className="h-11 rounded-xl bg-[#f7f4ec] border-stone-300 focus-visible:ring-stone-900"
              />
            </Field>

            <Field label="Work email" error={errors.work_email}>
              <Input
                data-testid="form-work-email"
                type="email"
                value={form.work_email}
                onChange={(e) => update("work_email", e.target.value)}
                placeholder="jane@firm.com"
                autoComplete="email"
                className="h-11 rounded-xl bg-[#f7f4ec] border-stone-300 focus-visible:ring-stone-900"
              />
            </Field>

            <Field label="Law firm" error={errors.law_firm}>
              <Input
                data-testid="form-law-firm"
                value={form.law_firm}
                onChange={(e) => update("law_firm", e.target.value)}
                placeholder="Firm name"
                autoComplete="organization"
                className="h-11 rounded-xl bg-[#f7f4ec] border-stone-300 focus-visible:ring-stone-900"
              />
            </Field>

            <Field label="Role" error={errors.role}>
              <Input
                data-testid="form-role"
                value={form.role}
                onChange={(e) => update("role", e.target.value)}
                placeholder="Partner, BD Director, Associate…"
                className="h-11 rounded-xl bg-[#f7f4ec] border-stone-300 focus-visible:ring-stone-900"
              />
            </Field>

            <Field label="Country" error={errors.country}>
              <Input
                data-testid="form-country"
                value={form.country}
                onChange={(e) => update("country", e.target.value)}
                placeholder="Mexico"
                className="h-11 rounded-xl bg-[#f7f4ec] border-stone-300 focus-visible:ring-stone-900"
              />
            </Field>

            <Field label="Target directory" error={errors.target_directory}>
              <Select
                value={form.target_directory}
                onValueChange={(v) => update("target_directory", v)}
              >
                <SelectTrigger
                  data-testid="form-target-directory"
                  className="h-11 rounded-xl bg-[#f7f4ec] border-stone-300"
                >
                  <SelectValue placeholder="Select a directory" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Chambers & Partners">
                    Chambers &amp; Partners
                  </SelectItem>
                  <SelectItem value="The Legal 500">The Legal 500</SelectItem>
                  <SelectItem value="Both">Both</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field label="Practice area" error={errors.practice_area}>
              <Input
                data-testid="form-practice-area"
                value={form.practice_area}
                onChange={(e) => update("practice_area", e.target.value)}
                placeholder="e.g. M&A, Banking, Disputes"
                className="h-11 rounded-xl bg-[#f7f4ec] border-stone-300 focus-visible:ring-stone-900"
              />
            </Field>

            <Field
              label="Approximate next submission cycle"
              error={errors.next_submission_cycle}
            >
              <Input
                data-testid="form-next-cycle"
                value={form.next_submission_cycle}
                onChange={(e) =>
                  update("next_submission_cycle", e.target.value)
                }
                placeholder="e.g. Chambers Latin America 2027, The Legal 500 Mexico 2026"
                className="h-11 rounded-xl bg-[#f7f4ec] border-stone-300 focus-visible:ring-stone-900"
              />
            </Field>
          </div>

          <div className="mt-5">
            <Field
              label="Main challenge with submissions"
              error={errors.main_challenge}
            >
              <Textarea
                data-testid="form-main-challenge"
                value={form.main_challenge}
                onChange={(e) => update("main_challenge", e.target.value)}
                placeholder="What is the hardest part of preparing your submissions today? (No confidential information.)"
                rows={4}
                className="rounded-xl bg-[#f7f4ec] border-stone-300 focus-visible:ring-stone-900"
              />
            </Field>
          </div>

          <div className="mt-7 rounded-2xl bg-[#f7f4ec] border border-stone-200 p-5 flex items-start gap-3">
            <Checkbox
              id="confidentiality_ack"
              data-testid="form-confidentiality-ack"
              checked={form.confidentiality_ack}
              onCheckedChange={(c) => update("confidentiality_ack", !!c)}
              className="mt-0.5 border-stone-400 data-[state=checked]:bg-stone-900 data-[state=checked]:border-stone-900"
            />
            <div>
              <Label
                htmlFor="confidentiality_ack"
                className="text-sm text-stone-900 font-medium leading-relaxed"
              >
                I understand this form should not include confidential matter
                details, client names, documents or case information.
              </Label>
              {errors.confidentiality_ack && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.confidentiality_ack}
                </p>
              )}
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-stone-600">
              <ShieldCheckIcon
                size={16}
                weight="duotone"
                className="text-stone-700"
              />
              No confidential information collected here.
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="form-submit"
              className="h-12 px-8 rounded-full bg-stone-900 hover:bg-stone-800 text-[#f7f4ec] font-medium disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Request early access"}
              {!submitting && (
                <ArrowRightIcon size={16} weight="bold" className="ml-2" />
              )}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <Label className="block text-sm font-medium text-stone-700 mb-2">
        {label}
      </Label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
